export interface User {
  id: string
  /** Null for Sign in with Apple accounts where Apple withheld the address. */
  email: string | null
  passwordHash?: string
  name?: string
  avatarUrl?: string
  createdAt: string
  updatedAt: string
  /**
   * Per-user feature flag: shows the Drawing (PencilKit) toolbar item in the
   * native app. Optional because older cached payloads predate the column;
   * absent means off.
   */
  drawingEnabled?: boolean
}

export interface OAuthAccount {
  id: string
  userId: string
  provider: 'google' | 'apple' | 'github'
  providerUserId: string
  createdAt: string
}

export interface Session {
  id: string
  userId: string
  expiresAt: string
  createdAt: string
}

export interface Note {
  id: string
  userId: string
  title: string
  content: string
  isPinned: boolean
  isEncrypted: boolean
  encryptionHint?: string
  wordCount: number
  tags: string[]
  createdAt: string
  updatedAt: string
  deletedAt?: string
  archivedAt?: string
  sharedAt?: string | null
  /**
   * Manual-order key for the "Manual" sort mode. Epoch-ms scale, sorted DESC
   * (higher = top). null/undefined falls back to createdAt. Update-backed
   * sync, like note pin — see manualOrderBetween in @caliu/utils.
   */
  sortOrder?: number | null
  /**
   * Read-only ("Make Read-Only"): the editors refuse text input so a stray tap
   * can't raise the keyboard and mutate a reference note. A client-side
   * ergonomics guard, NOT a permission — the API never rejects a write on a
   * read-only note, so MCP agents, Shortcuts and share-append keep working.
   * Update-backed sync, like note pin: a normal `update` op carrying
   * `is_read_only`, and the server must not bump `updated_at` for it.
   * Optional because older cached payloads predate the column; absent means off.
   */
  isReadOnly?: boolean
}

export interface Tag {
  id: string
  userId: string
  name: string
  parentId?: string
  color?: string | null
  icon?: string | null
  /** Pinned tags sort first in the sidebar. Update-backed sync, like note pin. */
  isPinned?: boolean
  createdAt: string
}

export interface NoteTag {
  noteId: string
  tagId: string
}

export interface Attachment {
  id: string
  userId: string
  filename: string
  contentType: string
  sizeBytes: number
  r2Key: string
  createdAt: string
}

export interface Reminder {
  id: string
  noteId: string
  userId: string
  triggerAt: string
  recurrence?: string
  snoozedUntil?: string
  firedAt?: string
  dismissedAt?: string
  createdAt: string
  updatedAt: string
}

export interface FrontmatterReminder {
  id: string
  triggerAt: string
  recurrence?: string | null
  snoozedUntil?: string | null
  firedAt?: string | null
  dismissedAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  title?: string | null
}

export interface ApiKey {
  id: string
  userId: string
  name: string
  keyHash: string
  prefix: string
  lastUsedAt?: string
  expiresAt?: string
  createdAt: string
}

export interface DeviceToken {
  id: string
  userId: string
  token: string
  platform: 'ios' | 'macos' | 'web' | 'android'
  createdAt: string
  updatedAt: string
}

export type DevicePlatform = 'web' | 'ios' | 'ipados' | 'macos' | 'android'

/**
 * A logged-in session, surfaced to the user as a "device" in Settings.
 * Backed by the `sessions` table extended with platform/geo/last-used metadata.
 */
export interface DeviceSession {
  id: string
  platform: DevicePlatform | null
  userAgent: string | null
  country: string | null
  city: string | null
  lastUsedAt: string
  createdAt: string
  /** True when this row corresponds to the caller's own session. */
  current: boolean
}

/**
 * A historical snapshot of a note, projected from the `sync_log` table.
 * Updates within a 5-minute compaction window collapse into one entry.
 */
export interface NoteHistoryEntry {
  version: number
  operation: 'create' | 'update' | 'soft-delete' | 'restore'
  createdAt: string
  title: string
  content: string
  isEncrypted: boolean
  encryptionHint: string | null
}

export interface NoteLink {
  sourceNoteId: string
  targetNoteId: string
}

export interface NoteVersion {
  id: string
  noteId: string
  content: string
  title: string
  createdAt: string
}

/**
 * Caliu Pro entitlement, as returned by `GET /subscription`. `isPro` is the
 * single value every client gates on; it is derived server-side from the
 * RevenueCat subscription's expiry plus the grandfathered flag. The client may
 * OR it with the RevenueCat SDK's live entitlement for an instant unlock right
 * after an in-app purchase, before the webhook reaches the server.
 */
export interface Entitlement {
  isPro: boolean
  source:
    | 'grandfathered'
    | 'app_store'
    | 'play_store'
    | 'stripe'
    | 'promotional'
    | 'none'
  /** ISO 8601. null for grandfathered / lifetime / no subscription. */
  expiresAt: string | null
  willRenew: boolean
  /**
   * What kind of purchase backs a paid entitlement: a renewing subscription
   * or the one-time Pro lifetime purchase (a non-consumable, no expiry). Both
   * unlock exactly the same features; the client only uses it for copy
   * ("Renews on ..." vs "Lifetime access") and for the `plan` profile
   * property. `null` when there is no store purchase (free, grandfathered,
   * promotional). Additive: clients that do not read it are unaffected.
   */
  plan: EntitlementPlan | null
  /**
   * The free note limit that applies to this account, or `null` for
   * unlimited. Unlimited for Pro, grandfathered, and every account created
   * before `NOTE_LIMIT_CUTOFF`; `FREE_NOTE_LIMIT` otherwise. Derived on the
   * server from the account's creation date so nothing is stored or
   * backfilled. It is a UI guard exactly like `isReadOnly`: the server never
   * rejects a note write and sync stays ungated (`syncNotGated.test.ts`).
   */
  noteLimit: number | null
  /** Notes counting toward `noteLimit`, or `null` when it is. Read by the MCP server's `create_note` guard. */
  noteCount?: number | null
}

/** See `Entitlement.plan`. */
export type EntitlementPlan = 'subscription' | 'lifetime'

/**
 * Free note limit for new accounts and new local-mode devices: notes outside
 * the trash (active + archived). Archiving is not a way around the limit; the
 * trash does not count because it auto-empties after 30 days.
 *
 * Shared by the worker (`GET /subscription` reports it), the PWA in local
 * mode, and Swift (`NoteLimit.swift`, held in parity by a test).
 */
export const FREE_NOTE_LIMIT = 50

/**
 * Day D of the lifetime / note-limit release, as an ISO instant. Accounts
 * created before it, and devices that already held notes before it, keep
 * unlimited notes forever. Before this instant passes nobody has a limit at
 * all, which is what lets the code ship early behind the date. It is the only
 * date in the plan and it is not public. Mirrored in Swift (`NoteLimit.swift`).
 */
export const NOTE_LIMIT_CUTOFF = '2026-10-09T00:00:00.000Z'

/**
 * Pointer record on the server linking a recipient user to a note shared by
 * another user. Existence in `shared_note_refs` means "user U has note N in
 * their Shared with me list". Only the (user, note) pair travels through
 * sync — note content is fetched on demand from `GET /public/share/:id`.
 *
 * Lifecycle:
 *  - Client creates a ref via POST /sync/push when the user follows a share
 *    deep link (validated server-side: target must be shared, alive, and not
 *    owned by the requester).
 *  - Client deletes a ref via POST /sync/push when the user removes the entry.
 *  - The server fans out a `delete` op to every recipient holding a ref when
 *    the owner unshares (clears `sharedAt`) or trashes/hard-deletes the note.
 *    No auto-restore on owner restore — recipients re-add manually.
 */
export interface SharedNoteRef {
  id: string
  userId: string
  noteId: string
  addedAt: string
}

// ── Sync Protocol Types ────────────────────────────────────────────────────
// These types define the cross-platform sync contract. Any client (web, iOS,
// macOS) must implement the same protocol to interoperate with the Caliu API.

export type SyncStatus = 'synced' | 'pending' | 'conflict'

export type SyncState = 'idle' | 'syncing' | 'error'

/** Entity types that participate in the sync protocol. */
export type SyncEntity =
  | 'note'
  | 'tag'
  | 'note_tag'
  | 'note_link'
  | 'note_version'
  | 'shared_note_ref'

export interface SyncOperation {
  id?: number
  type: 'create' | 'update' | 'delete' | 'soft-delete' | 'restore'
  entity: SyncEntity
  entityId: string
  data?: unknown
  timestamp: number
  retries: number
}

export interface SyncResult {
  synced: number
  failed: number
  skipped: number
}

// ── New Sync Protocol (v2) — SQLite Everywhere ───────────────────────────
// Operation log-based sync protocol. Clients push local operations and pull
// changes since their last known server version. All entities use UUIDs
// generated client-side — no more `local_` temporary IDs.

/** A single change entry in the sync log (server-side). */
export interface SyncLogEntry {
  id: number
  userId: string
  entity: SyncEntity
  entityId: string
  operation: 'create' | 'update' | 'delete' | 'soft-delete' | 'restore'
  /** Server-assigned monotonic version per user. */
  version: number
  /** JSON snapshot of the entity data at this version. */
  data: string
  createdAt: string
}

/** Client → Server: batch of local operations to push. */
export interface SyncPushRequest {
  operations: SyncPushOperation[]
}

export interface SyncPushOperation {
  entity: SyncEntity
  entityId: string
  operation: 'create' | 'update' | 'delete' | 'soft-delete' | 'restore'
  data: Record<string, unknown>
  /** Client timestamp (ISO 8601) for conflict resolution. */
  clientTimestamp: string
}

/** Server → Client: result of a push operation. */
export interface SyncPushResponse {
  results: SyncPushResult[]
  /** The server version after applying all operations. */
  serverVersion: number
}

export interface SyncPushResult {
  entity: SyncEntity
  entityId: string
  status: 'applied' | 'conflict' | 'error'
  /** Server version assigned to this operation. */
  version?: number
  /** Server data (returned on conflict for client-side resolution). */
  serverData?: Record<string, unknown>
  error?: string
}

/** Client → Server: request changes since last known version. */
export interface SyncPullRequest {
  /** Last server version the client has seen. 0 = full pull. */
  sinceVersion: number
  /** Max number of changes to return (default 500). */
  limit?: number
}

/** Server → Client: batch of changes since the requested version. */
/**
 * The collaboration features the server turned on for this user (rollout
 * stage x the user's beta flags). `live` and `sharing` imply `syncV2`.
 */
export interface CollabFeatures {
  syncV2: boolean
  live: boolean
  sharing: boolean
}

export interface SyncPullResponse {
  changes: SyncChange[]
  /** Current server version (watermark for next pull). */
  serverVersion: number
  /** True if there are more changes to fetch. */
  hasMore: boolean
  /** The requester's collaboration features. Absent (older servers) reads as all false. */
  collabFeatures?: CollabFeatures
}

export interface SyncChange {
  entity: SyncEntity
  entityId: string
  operation: 'create' | 'update' | 'delete' | 'soft-delete' | 'restore'
  version: number
  data: Record<string, unknown>
  timestamp: string
}

// ── Sync Platform Interfaces ───────────────────────────────────────────────
// Abstract the platform-specific dependencies so the sync engine can be
// ported to iOS/macOS without reimplementing core logic.

export interface SyncStorage {
  getAllSyncOps(): Promise<SyncOperation[]>
  deleteSyncOp(id: number): Promise<void>
  updateSyncOpRetries(id: number, retries: number): Promise<void>
  upsertNotes(notes: Note[]): Promise<void>
  upsertTags(tags: Tag[]): Promise<void>
  getLastSyncTimestamp(): Promise<string | null>
  setLastSyncTimestamp(ts: string): Promise<void>
  updateNoteSyncStatus(noteId: string, status: SyncStatus): Promise<void>
  bulkDeleteNotes(ids: string[]): Promise<void>
  getAllLocalTags(): Promise<Array<Tag & { syncStatus?: SyncStatus }>>
  bulkDeleteTags(ids: string[]): Promise<void>
  /** Get the server version watermark for v2 sync pull. */
  getServerVersion(): Promise<number>
  /** Update the server version watermark after a successful pull. */
  setServerVersion(version: number): Promise<void>
  /** Mark an entity (note or tag) as 'synced' if currently 'pending'. */
  markEntitySynced(entity: string, entityId: string): Promise<void>
  /** Get a note by ID (for conflict resolution). */
  getNote(id: string): Promise<Note | undefined>
  /** Reconcile a client-generated tag ID with its server-assigned ID. */
  reconcileTagId(clientId: string, serverId: string): Promise<void>
  /** Delete sync ops that have been superseded by deduplication. */
  deleteSupersededOps(ops: SyncOperation[]): Promise<void>
  /** Apply pull changes atomically and update the server version watermark. */
  applyPullChanges(changes: SyncChange[], serverVersion: number, userId: string): Promise<void>
  /** Mark a note as having a conflict. */
  markNoteConflict(noteId: string): Promise<void>
  /** Read a note's stored merge base (last-synced content), or null if none. */
  getNoteBaseContent(noteId: string): Promise<string | null>
  /**
   * Write an auto-merged note locally and re-enqueue it for push. The re-push
   * op carries `expectedUpdatedAt` (the server's current timestamp) so it
   * applies cleanly or re-conflicts into another merge round.
   */
  applyAutoMergedNote(noteId: string, mergedContent: string, expectedUpdatedAt: string): Promise<void>
  /** Put a full note (for conflict resolution — server wins). */
  putNote(note: Note & { syncStatus: SyncStatus }): Promise<void>
  /** Mark a note as synced (for conflict resolution — local wins). */
  markNoteSynced(noteId: string): Promise<void>
  /** Get all notes (for resolving all conflicts). */
  getAllNotes(): Promise<Array<Note & { syncStatus: SyncStatus }>>
  /** Get pending ops count. */
  getPendingCount(): Promise<number>
  /** Get failed ops count (retries >= maxRetries). */
  getFailedCount(maxRetries: number): Promise<number>
  /** Reset retries on failed ops. */
  retryFailedOps(maxRetries: number): Promise<void>
  /** Delete all failed ops. */
  clearFailedOps(maxRetries: number): Promise<void>
  /** Get count of notes with conflict status. */
  getConflictCount(): Promise<number>
  /** Clean up zombie pending states (entities marked pending with no sync ops). */
  cleanupZombiePendingStates(): Promise<void>
}

export interface SyncHttpClient {
  /** Non-throwing fetch — returns null on error. */
  fetch<T>(path: string, options?: { method?: string; body?: string }): Promise<T | null>
  /** Throwing fetch — throws ApiError on non-2xx. */
  fetchOrThrow<T>(path: string, options?: { method?: string; body?: string }): Promise<T>
}

export interface SyncEventBus {
  emit(event: 'sync-state', state: SyncState): void
  subscribe(event: 'sync-state', callback: (state: SyncState) => void): () => void
}

// ── Font Preferences ───────────────────────────────────────────────────────
// Fonts are decoupled from themes. One font per UI region — flat model.

export type FontRegion = 'chrome' | 'prose' | 'headings' | 'code'

export interface FontPreferences {
  /** Font id to use for each region. */
  regions: Record<FontRegion, string>
}

export const FONT_REGIONS: readonly FontRegion[] = ['chrome', 'prose', 'headings', 'code'] as const

/** Default mapping applied to every user on first launch (no migration). */
export const DEFAULT_FONT_PREFERENCES: FontPreferences = {
  regions: {
    chrome: 'plus-jakarta-sans',
    prose: 'lora',
    headings: 'lora',
    code: 'jetbrains-mono',
  },
}

// ── Editor Typography ──────────────────────────────────────────────────────
// Editor-only: body size + line height + paragraph spacing, as the numbers the
// user sees. Affects the note editor's prose/headings/code rendering; does not
// touch app chrome. Ranges and the old preset names live in @caliu/utils.

export interface EditorTypography {
  /** Body text size in px (a point on Apple, a dp on Android). */
  size: number
  /** Body line height: baseline-to-baseline distance ÷ size. */
  lineHeight: number
  /** Gap after a paragraph, in px. Every other block gap scales with it. */
  paragraphSpacing: number
}

export const DEFAULT_EDITOR_TYPOGRAPHY: EditorTypography = {
  size: 17,
  lineHeight: 1.8,
  paragraphSpacing: 8,
}

// ── Locale ────────────────────────────────────────────────────────────────
// Shared across web, app, and Swift clients. Swift mirrors this list in
// LanguageManager.swift. Adding a locale here requires adding catalogs in
// apps/app/src/locales, apps/web/messages, and apps/swift Localizable.xcstrings.

export type Locale =
  | 'en'
  | 'es'
  | 'ca'
  | 'fr'
  | 'de'
  | 'it'
  | 'pt'
  | 'nl'
  | 'ja'
  | 'zh-CN'
  | 'ko'
  | 'zh-TW'
  | 'ru'

export const SUPPORTED_LOCALES: readonly Locale[] = [
  'en',
  'es',
  'ca',
  'fr',
  'de',
  'it',
  'pt',
  'nl',
  'ja',
  'zh-CN',
  'ko',
  'zh-TW',
  'ru',
] as const

export const DEFAULT_LOCALE: Locale = 'en'

export interface LocaleMeta {
  code: Locale
  nativeName: string
  englishName: string
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  'en':    { code: 'en',    nativeName: 'English',    englishName: 'English' },
  'es':    { code: 'es',    nativeName: 'Español',    englishName: 'Spanish' },
  'ca':    { code: 'ca',    nativeName: 'Català',     englishName: 'Catalan' },
  'fr':    { code: 'fr',    nativeName: 'Français',   englishName: 'French' },
  'de':    { code: 'de',    nativeName: 'Deutsch',    englishName: 'German' },
  'it':    { code: 'it',    nativeName: 'Italiano',   englishName: 'Italian' },
  'pt':    { code: 'pt',    nativeName: 'Português',  englishName: 'Portuguese' },
  'nl':    { code: 'nl',    nativeName: 'Nederlands', englishName: 'Dutch' },
  'ja':    { code: 'ja',    nativeName: '日本語',      englishName: 'Japanese' },
  'zh-CN': { code: 'zh-CN', nativeName: '简体中文',    englishName: 'Chinese (Simplified)' },
  'ko':    { code: 'ko',    nativeName: '한국어',      englishName: 'Korean' },
  'zh-TW': { code: 'zh-TW', nativeName: '繁體中文',    englishName: 'Chinese (Traditional)' },
  'ru':    { code: 'ru',    nativeName: 'Русский',    englishName: 'Russian' },
}

// ── Help center ────────────────────────────────────────────────────────────
// Help content is authored as markdown in apps/worker/content/help/<locale>/,
// compiled into the worker bundle, and served by GET /public/help. All three
// clients (web, app, Swift) consume these shapes; Swift mirrors them in
// CaliuCore. `version` is a content hash so clients can cheap-check for
// updates (ETag / If-None-Match) and cache the whole payload as one blob.

export interface HelpArticle {
  slug: string
  title: string
  summary: string
  order: number
  body: string
}

export interface HelpSection {
  slug: string
  title: string
  description: string
  /** Semantic icon key (e.g. 'rocket', 'pencil') mapped per client. */
  icon: string
  articles: HelpArticle[]
}

export interface HelpContent {
  version: string
  sections: HelpSection[]
}

export interface HelpPayload extends HelpContent {
  /** The effective locale of the returned content (after fallback). */
  locale: Locale
}
